import { Injectable } from '@angular/core';
import { Apollo } from 'apollo-angular';
import { LaunchpadUser, LaunchpadUserDto } from '../models/launchpad-user';
import { map, Observable } from 'rxjs';
import { Queries } from '../queries';
import { UserInput } from '../models/user-input';
import { ExportFormat } from 'aih-launchpad-core';
import { ExportFile } from '../models/export-file';

@Injectable({
  providedIn: 'root'
})
export class UsersAdminService {

  constructor(private apollo: Apollo) { }

  getUsersForOrganisation(
    organisations: string[] | null,
    canImpersonate: boolean = false,
    searchTerm: string = '',
    onlyActive: boolean,
    onlyImpersonationRequested: boolean
  ): Observable<LaunchpadUser[]> {
    return this.apollo.watchQuery<{organisationUsers: LaunchpadUserDto[]}>({
      query: Queries.GET_USERS,
      fetchPolicy: 'no-cache',
      variables: {
        canImpersonate: canImpersonate,
        ...this.getUserFilterVariables(organisations, canImpersonate, searchTerm, onlyActive, onlyImpersonationRequested)
      }
    }).valueChanges.pipe(map(result => {
      let users = result.data.organisationUsers.map(user => LaunchpadUser.fromLaunchpadUserDto(user));

      return users ?? [];
    }))
  }

  /**
   * Exports the users as an Excel or CSV file. Takes the same filters as getUsersForOrganisation, so
   * passing the users table's current filters exports exactly what is shown.
   */
  getUsersExport(
    exportType: ExportFormat,
    language: string,
    organisations: string[] | null,
    canImpersonate: boolean = false,
    searchTerm: string = '',
    onlyActive: boolean = false,
    onlyImpersonationRequested: boolean = false
  ): Observable<ExportFile> {
    return this.apollo.query<{organisationUsersExport: ExportFile}>({
      query: Queries.GET_USERS_EXPORT,
      fetchPolicy: 'no-cache',
      variables: {
        exportType: exportType,
        language: language,
        timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        ...this.getUserFilterVariables(organisations, canImpersonate, searchTerm, onlyActive, onlyImpersonationRequested)
      }
    }).pipe(map(result => result.data.organisationUsersExport))
  }

  downloadExportFile(file: ExportFile): void {
    const bytes = Uint8Array.from(atob(file.base64EncodedContent), c => c.charCodeAt(0));
    const url = URL.createObjectURL(new Blob([bytes], { type: file.contentType }));

    const link = document.createElement('a');
    link.href = url;
    link.download = file.fileName;
    link.click();

    // Give the browser a moment to start the download before releasing the file
    setTimeout(() => URL.revokeObjectURL(url));
  }

  private getUserFilterVariables(
    organisations: string[] | null,
    canImpersonate: boolean,
    searchTerm: string,
    onlyActive: boolean,
    onlyImpersonationRequested: boolean
  ) {
    return {
      organisations: organisations,
      searchTerm: searchTerm,
      activeStatusQuery: onlyActive
        ? { isActive: { eq: true } }
        : { },
      impersonationQuery: canImpersonate && onlyImpersonationRequested
        ? { impersonationExpiry: { gt: new Date() } }
        : { }
    };
  }

  getUser(userId: string): Observable<LaunchpadUser> {
    return this.apollo.watchQuery<{user: LaunchpadUserDto}>({
      query: Queries.GET_USER,
      fetchPolicy: 'no-cache',
      variables: {
        userId: userId
      }
    }).valueChanges.pipe(map(result => LaunchpadUser.fromLaunchpadUserDto(result.data.user)))
  }

  createUser(user: Partial<UserInput>): Observable<LaunchpadUser | undefined> {
    return this.apollo.mutate<{createUser: LaunchpadUser}>({
      mutation: Queries.CREATE_USER,
      variables: {
        user: user
      }
    }).pipe(map(result => result.data?.createUser))
  }

  updateUser(user: Partial<UserInput>): Observable<LaunchpadUser | undefined> {
    return this.apollo.mutate<{updateUser: LaunchpadUser}>({
      mutation: Queries.UPDATE_USER,
      variables: {
        user: user
      }
    }).pipe(map(result => result.data?.updateUser))
  }
  
  activateUser(userEmail: string): Observable<LaunchpadUser | undefined> {
    return this.apollo.mutate<{activateUser: LaunchpadUser}>({
      mutation: Queries.ACTIVATE_USER,
      variables: {
        userEmail: userEmail
      }
    }).pipe(map(result => result.data?.activateUser))
  }

  deactivateUser(userEmail: string): Observable<LaunchpadUser | undefined> {
    return this.apollo.mutate<{deactivateUser: LaunchpadUser}>({
      mutation: Queries.DEACTIVATE_USER,
      variables: {
        userEmail: userEmail
      }
    }).pipe(map(result => result.data?.deactivateUser))
  }

  resendUserInvite(userEmail: string): Observable<LaunchpadUser | undefined> {
    return this.apollo.mutate<{inviteUser: LaunchpadUser}>({
      mutation: Queries.RESEND_USER_INVITE,
      variables: {
        userEmail: userEmail
      }
    }).pipe(map(result => result.data?.inviteUser))
  }
}
